import { useMutation, useQuery } from '@tanstack/react-query';
import { Eye, PiggyBank, Plus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { queryClient, trpc, trpcClient } from '@/utils/trpc';
import { CategoryCreator } from './category-creator';

interface BudgetFormData {
  categoryId?: string;
  amount: string;
  alertThreshold: number;
}

export function BudgetCreator() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isViewCategoriesOpen, setIsViewCategoriesOpen] = useState(false);
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [formData, setFormData] = useState<BudgetFormData>({
    categoryId: undefined,
    amount: '',
    alertThreshold: 80,
  });

  // Queries
  const categories = useQuery(
    trpc.categories.list.queryOptions({ type: 'expense' })
  );
  const budgets = useQuery(
    trpc.budgets.list.queryOptions({ period: 'monthly' })
  );

  // Mutations
  const createBudget = useMutation({
    mutationFn: (data: any) => trpcClient.budgets.create.mutate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['budgets', 'list', { period: 'monthly' }],
      });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Erro ao criar orçamento:', error.message);
      alert(`Erro ao criar orçamento: ${error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      categoryId: undefined,
      amount: '',
      alertThreshold: 80,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || Number.parseFloat(formData.amount) <= 0) {
      alert('Digite um valor válido para o orçamento');
      return;
    }

    const budgetData = {
      ...formData,
      period: 'monthly' as const,
      startDate: new Date(),
      isShared: true,
      rollover: false,
    };

    createBudget.mutate(budgetData);
  };

  const categoriesData = categories.data || [];

  // Budget templates
  const budgetTemplates = [
    {
      name: '50/30/20 Clássico',
      description: '50% necessidades, 30% desejos, 20% poupança/dívidas',
      icon: '📊',
      categories: [
        { name: 'Moradia', icon: '🏠', type: 'expense', percentage: 25 },
        { name: 'Alimentação', icon: '🍕', type: 'expense', percentage: 15 },
        { name: 'Transporte', icon: '🚗', type: 'expense', percentage: 10 },
        { name: 'Lazer', icon: '🎮', type: 'expense', percentage: 15 },
        { name: 'Compras', icon: '🛍️', type: 'expense', percentage: 15 },
        { name: 'Poupança', icon: '💰', type: 'expense', percentage: 20 },
      ],
    },
    {
      name: '60/20/20 Conservador',
      description: '60% essenciais, 20% lazer, 20% poupança',
      icon: '🔒',
      categories: [
        { name: 'Moradia', icon: '🏠', type: 'expense', percentage: 30 },
        { name: 'Alimentação', icon: '🍕', type: 'expense', percentage: 20 },
        { name: 'Transporte', icon: '🚗', type: 'expense', percentage: 10 },
        { name: 'Lazer', icon: '🎮', type: 'expense', percentage: 10 },
        { name: 'Compras', icon: '🛍️', type: 'expense', percentage: 10 },
        { name: 'Poupança', icon: '💰', type: 'expense', percentage: 20 },
      ],
    },
    {
      name: '70/20/10 Iniciante',
      description: '70% gastos, 20% poupança, 10% investimentos',
      icon: '🌱',
      categories: [
        { name: 'Moradia', icon: '🏠', type: 'expense', percentage: 30 },
        { name: 'Alimentação', icon: '🍕', type: 'expense', percentage: 20 },
        { name: 'Transporte', icon: '🚗', type: 'expense', percentage: 10 },
        { name: 'Contas', icon: '📄', type: 'expense', percentage: 10 },
        { name: 'Poupança', icon: '💰', type: 'expense', percentage: 20 },
        { name: 'Investimentos', icon: '📈', type: 'expense', percentage: 10 },
      ],
    },
    {
      name: '60/20/10/10 com Dízimo',
      description: '10% dízimo, 60% gastos essenciais, 20% poupança, 10% lazer',
      icon: '🙏',
      categories: [
        { name: 'Dízimo', icon: '🙏', type: 'expense', percentage: 10 },
        { name: 'Moradia', icon: '🏠', type: 'expense', percentage: 25 },
        { name: 'Alimentação', icon: '🍕', type: 'expense', percentage: 20 },
        { name: 'Transporte', icon: '🚗', type: 'expense', percentage: 10 },
        { name: 'Contas', icon: '📄', type: 'expense', percentage: 5 },
        { name: 'Lazer', icon: '🎮', type: 'expense', percentage: 10 },
        { name: 'Poupança', icon: '💰', type: 'expense', percentage: 20 },
      ],
    },
  ];

  const applyTemplate = async (template: (typeof budgetTemplates)[0]) => {
    if (!monthlyIncome || Number.parseFloat(monthlyIncome) <= 0) {
      alert('Digite sua renda mensal primeiro');
      return;
    }

    const income = Number.parseFloat(monthlyIncome);

    // Create categories and budgets for the template
    for (const cat of template.categories) {
      // Check if category exists
      let categoryId = categoriesData.find((c) => c.name === cat.name)?.id;

      // If not, create it
      if (!categoryId) {
        try {
          const newCategory = await trpcClient.categories.create.mutate({
            name: cat.name,
            icon: cat.icon,
            type: cat.type as 'expense' | 'income',
            color: cat.type === 'expense' ? '#ef4444' : '#22c55e',
          });
          categoryId = newCategory.id;
          // Refresh categories
          categories.refetch();
        } catch (error) {
          console.error('Erro ao criar categoria:', error);
          continue;
        }
      }

      // Calculate budget amount
      const budgetAmount = (income * cat.percentage) / 100;

      // Create budget
      try {
        await trpcClient.budgets.create.mutate({
          categoryId,
          amount: budgetAmount.toString(),
          period: 'monthly',
          startDate: new Date(),
          alertThreshold: 80,
          rollover: false,
          isShared: true,
        });
      } catch (error) {
        console.error('Erro ao criar orçamento:', error);
      }
    }

    // Refresh budgets
    budgets.refetch();
    setIsTemplateDialogOpen(false);
    setMonthlyIncome('');
    alert(`Template "${template.name}" aplicado com sucesso!`);
  };

  console.log(categoriesData);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="h-5 w-5" />
              Criar Orçamento
            </CardTitle>
            <CardDescription>
              Configure orçamentos mensais para suas categorias
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <CategoryCreator compact />

            <Dialog
              onOpenChange={setIsViewCategoriesOpen}
              open={isViewCategoriesOpen}
            >
              <DialogTrigger asChild>
                <Button className="gap-2" variant="outline">
                  <Eye className="h-4 w-4" />
                  Ver Categorias
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Categorias Existentes</DialogTitle>
                  <DialogDescription>
                    Visualize todas as suas categorias criadas
                  </DialogDescription>
                </DialogHeader>

                <div className="max-h-96 overflow-y-auto">
                  {categories.isPending ? (
                    <div className="space-y-2">
                      {[...Array(6)].map((_, i) => (
                        <div
                          className="h-16 animate-pulse rounded bg-muted"
                          key={i}
                        />
                      ))}
                    </div>
                  ) : categoriesData.length === 0 ? (
                    <div className="py-12 text-center">
                      <PiggyBank className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                      <h3 className="mb-2 font-semibold">
                        Nenhuma categoria encontrada
                      </h3>
                      <p className="mb-4 text-muted-foreground text-sm">
                        Crie sua primeira categoria para começar
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {categoriesData.map((category) => (
                        <div
                          className="flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-gray-50"
                          key={category.id}
                        >
                          <span className="text-2xl">{category.icon}</span>
                          <div
                            className="h-4 w-4 rounded-full border"
                            style={{
                              backgroundColor: category.color ?? '#B2BEC3',
                            }}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h4 className="font-medium">{category.name}</h4>
                              <Badge className="text-xs" variant="outline">
                                {category.type === 'expense'
                                  ? 'Gasto'
                                  : category.type === 'income'
                                    ? 'Receita'
                                    : category.type === 'transfer'
                                      ? 'Transferência'
                                      : 'Investimento'}
                              </Badge>
                            </div>
                            <p className="mt-1 text-muted-foreground text-sm">
                              ID: {category.id.slice(-8)}
                              {(category as any).transactionCount > 0 && (
                                <span>
                                  {' '}
                                  • {(category as any).transactionCount}{' '}
                                  transações
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-sm">
                              {category.isActive ? 'Ativa' : 'Inativa'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    onClick={() => setIsViewCategoriesOpen(false)}
                    variant="outline"
                  >
                    Fechar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              onOpenChange={setIsTemplateDialogOpen}
              open={isTemplateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button className="gap-2" variant="outline">
                  <Sparkles className="h-4 w-4" />
                  Usar Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Escolher Template de Orçamento</DialogTitle>
                  <DialogDescription>
                    Selecione um template baseado em estratégias populares de
                    orçamento pessoal
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Income Input */}
                  <div className="space-y-2">
                    <Label htmlFor="income">Sua Renda Mensal (R$)</Label>
                    <Input
                      className="w-full"
                      id="income"
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      placeholder="5000.00"
                      type="number"
                      value={monthlyIncome}
                    />
                    <p className="text-muted-foreground text-sm">
                      Digite sua renda mensal para calcular os valores dos
                      orçamentos
                    </p>
                  </div>

                  {/* Templates */}
                  <div className="space-y-3">
                    {budgetTemplates.map((template, index) => (
                      <div
                        className="cursor-pointer rounded-lg border p-4 transition-colors hover:bg-gray-50"
                        key={index}
                        onClick={() => applyTemplate(template)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{template.icon}</span>
                              <h3 className="font-semibold">{template.name}</h3>
                            </div>
                            <p className="text-muted-foreground text-sm">
                              {template.description}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {template.categories.map((cat, i) => (
                                <Badge
                                  className="text-xs"
                                  key={i}
                                  variant="secondary"
                                >
                                  {cat.icon} {cat.name}: {cat.percentage}%
                                </Badge>
                              ))}
                            </div>
                          </div>
                          {monthlyIncome &&
                            Number.parseFloat(monthlyIncome) > 0 && (
                              <div className="text-right">
                                <p className="font-medium text-muted-foreground text-sm">
                                  Total mensal
                                </p>
                                <p className="font-semibold text-lg">
                                  R${' '}
                                  {Number.parseFloat(monthlyIncome).toFixed(2)}
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    onClick={() => {
                      setIsTemplateDialogOpen(false);
                      setMonthlyIncome('');
                    }}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              onOpenChange={setIsCreateDialogOpen}
              open={isCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Novo Orçamento
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Orçamento Mensal</DialogTitle>
                    <DialogDescription>
                      Configure um orçamento para controlar seus gastos mensais
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="category">Categoria</Label>
                      <Select
                        onValueChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            categoryId: value === 'general' ? undefined : value,
                          }))
                        }
                        value={formData.categoryId || 'general'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">
                            Orçamento Geral
                          </SelectItem>
                          {categoriesData.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.icon} {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="amount">Valor Mensal (R$)</Label>
                      <Input
                        id="amount"
                        min="0"
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        placeholder="1000.00"
                        required
                        step="0.01"
                        type="number"
                        value={formData.amount}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="threshold">
                        Alerta quando atingir {formData.alertThreshold}% do
                        orçamento
                      </Label>
                      <Input
                        className="w-full"
                        id="threshold"
                        max="100"
                        min="50"
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            alertThreshold: Number(e.target.value),
                          }))
                        }
                        step="5"
                        type="range"
                        value={formData.alertThreshold}
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        resetForm();
                      }}
                      type="button"
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                    <Button disabled={createBudget.isPending} type="submit">
                      {createBudget.isPending
                        ? 'Criando...'
                        : 'Criar Orçamento'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {categoriesData.length === 0 ? (
          <div className="py-8 text-center">
            <PiggyBank className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 font-semibold">
              Configure suas categorias primeiro
            </h3>
            <p className="text-muted-foreground text-sm">
              Crie categorias de gastos para poder associar orçamentos a elas.
            </p>
          </div>
        ) : (
          <div className="py-4 text-center">
            <p className="text-muted-foreground text-sm">
              {categoriesData.length} categorias disponíveis para orçamento
            </p>
          </div>
        )}

        {createBudget.error && (
          <div className="mt-4 rounded-md border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">
              Erro: {createBudget.error.message}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
